import { NgClass, NgStyle } from "@angular/common";
import {
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { BlockComponent } from "@components/container-block/block.component";
import { HeaderComponent } from "@components/header/header.component";
import { NgbModal, NgbTooltip } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";
import _ from "lodash";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { EventService } from "~app/common/services/cqs/event.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { ArbetsorderListHoverInMessage, ArbetsorderListHoverOutMessage, ArbetsorderMapHoverInMessage, ArbetsorderMapHoverOutMessage } from "../../events/ArbetsorderHoverMessage";
import { ArbetsorderTransportforetagUpdatedMessage } from "../../events/ArbetsorderTransportforetagUpdatedMessage";
import { HandelssortimentTransportforetagUpdatedMessage } from "../../events/HandelssortimentTransportforetagUpdatedMessage";
import { ITplArbetsorder } from "../../models/dtos";
import { TplArbetsorderViewModel } from "../../models/tplListViewModels";
import { TplService } from "../../services/tpl.service";
import { TplStateService } from "../../state/tpl-state.service";
import { TplArbetsorderInfoModalComponent } from "../tpl-arbetsorder-info-modal/tpl-arbetsorder-info-modal.component";
import { TplTilldelningWarningModalComponent } from "./components/tpl-tilldelning-warning-modal/tpl-tilldelning-warning-modal.component";
import { TplTransporttilldelningModalComponent } from "./components/tpl-transporttilldelning-modal/tpl-transporttilldelning-modal.component";

@Component({
    selector: "rask-tpl-transporttilldelning",
    templateUrl: "./tpl-transporttilldelning.component.html",
    styleUrls: ["./tpl-transporttilldelning.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        BlockComponent,
        HeaderComponent,
        ButtonComponent,
        CheckboxComponent,
        FormsModule,
        NgClass,
        NgStyle,
        NgbTooltip,
        TranslatePipe
    ]
})
export class TplTransporttilldelningComponent implements OnInit, OnDestroy {
    readonly tabName = "transporttilldelning";

    allSelected = false;
    arbetsorders: TplArbetsorderViewModel[];
    @Input() canEdit: boolean;
    canTilldela = false;
    nrOfSelectedAos = 0;

    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly eventService = inject(EventService);
    private readonly modalService = inject(NgbModal);
    private readonly ngUnsubscribe$ = new Subject<void>();
    private readonly tplService = inject(TplService);
    private readonly tplStateService = inject(TplStateService);

    aoDetails($event, arbetsorderId: number) {
        $event.preventDefault();

        const arbetsorder = this.arbetsorders.find(ao => ao.id === arbetsorderId);
        TplArbetsorderInfoModalComponent.open({
            arbetsorderId: arbetsorderId,
            isSelected: arbetsorder.selected,
            showSelected: true
        }, this.modalService, (result) => {
            if (arbetsorder.selected !== result.isSelected) {
                arbetsorder.selected =  result.isSelected;
                this.onCheckedChanged(arbetsorder.selected);
            }
        });
    }

    mouseOut(arbetsorderId: number) {
        this.arbetsorders.find(p => p.id === arbetsorderId).isHover = false;
        this.eventService.emit(new ArbetsorderListHoverOutMessage(arbetsorderId));
    }

    mouseOver(arbetsorderId: number) {
        this.arbetsorders.find(p => p.id === arbetsorderId).isHover = true;
        this.eventService.emit(new ArbetsorderListHoverInMessage(arbetsorderId));
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    ngOnInit() {
        const state = this.tplStateService.current;
        this.createViewModel(state.getArbetsordrar());

        state.currentArbetsordrar
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(current => {
                this.createViewModel(current);
            });

        state.selected
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(event => {
                if (this.arbetsorders && event.origin !== "transporttilldelning") {
                    this.arbetsorders.forEach(ao => {
                        ao.isSelected = event.selected.some(id => id === ao.id);
                    });
                    this.updateSelectedView();
                }
            });

        this.eventService.subscribeEvent<ArbetsorderTransportforetagUpdatedMessage>(ArbetsorderTransportforetagUpdatedMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((event: ArbetsorderTransportforetagUpdatedMessage) => {
                this.onUpdateArbetsordrar(event);
            });

        this.eventService.subscribeEvent<HandelssortimentTransportforetagUpdatedMessage>(HandelssortimentTransportforetagUpdatedMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((event: HandelssortimentTransportforetagUpdatedMessage) => {
                this.onUpdateArbetsorderHandelssortiment(event);
            });

        this.eventService.subscribeEvent<ArbetsorderMapHoverInMessage>(ArbetsorderMapHoverInMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((event: ArbetsorderMapHoverInMessage) => {
                this.arbetsorders.find(p => p.id === event.arbetsorderId).isHover = true;
                this.triggerDigestCycle();
            });

        this.eventService.subscribeEvent<ArbetsorderMapHoverOutMessage>(ArbetsorderMapHoverOutMessage.$type)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((event: ArbetsorderMapHoverOutMessage) => {
                this.arbetsorders.find(p => p.id === event.arbetsorderId).isHover = false;
                this.triggerDigestCycle();
            });
    }

    onAllSelectedChanged($event: boolean) {
        this.allSelected = $event;
        this.arbetsorders.forEach(x => x.isSelected = $event);
        this.updateSelectedView();
        this.signalSelected();
    }

    onCheckedChanged($event: any) {
        setTimeout(() => {
            this.updateSelectedView();
            if ($event != null) {
                this.signalSelected();
            }
        }, 200);
    }

    onUpdateArbetsorderHandelssortiment(event: HandelssortimentTransportforetagUpdatedMessage) {
        const viewmodel = this.arbetsorders.find(x => x.id === event.arbetsorderId);
        if (!viewmodel) {
            return;
        }

        viewmodel.setHandelssortimentTransportforetag(event.handelssortimentId, event.transportforetagId, event.befraktareId, event.egenTransport);

        this.tplStateService.current.modifyArbetsordrar([viewmodel.model]);
    }

    onUpdateArbetsordrar(event: ArbetsorderTransportforetagUpdatedMessage) {
        const affectedArbetsordrar = this.arbetsorders.filter(x => event.arbetsorderIds.some(x1 => x1 === x.id));
        affectedArbetsordrar.forEach(x => x.setTransportforetag(event.transportforetagId));

        event.arbetsordersWithValsWarning.forEach(x => {
            this.arbetsorders.find(ao => ao.id === x).warningEnabled = true;
        });

        this.tplStateService.current.modifyArbetsordrar(affectedArbetsordrar.map(x => x.model));
    }

    openDialog() {
        const hs = this.tplStateService.current.getSelectedHandelssortiment();
        const bransleValt = this.tplStateService.current.getTransportsatt().bransle;
        const transportforetag = ObjectUtils.deepCopy(this.tplStateService.current.getTransportforetag());
        TplTransporttilldelningModalComponent.open(transportforetag, this.arbetsorders.filter(a => a.isSelected), this.modalService, hs, bransleValt,
            (result) => {
                if(result === true && !bransleValt) {
                    this.arbetsorders.filter(x => x.isSelected).forEach(ao => {
                        ao.isSelected = false;
                    });
                    this.signalSelected();
                }
            }
        );
    }

    showValsInfo(arbetsorderId: number) {
        const ao = this.arbetsorders.find(x => x.id === arbetsorderId);
        ao.hasClickedWarning = true;
        TplTilldelningWarningModalComponent.open(`${ao.beteckning} ${ao.namn}`, this.modalService);
    }

    signalSelected() {
        const currentSelected = this.arbetsorders.filter(x => x.isSelected).map(x => x.model.id);
        this.tplStateService.current.setSelected({ selected: currentSelected,
            origin: "transporttilldelning" });
    }

    updateSelectedView() {
        this.setNrOfSelectedAos();
        this.sortArbetsorders();
    }

    private createViewModel(arbetsordrar: ITplArbetsorder[]) {
        const allTransportforetag = this.tplStateService.current.getTransportforetag();
        this.arbetsorders = arbetsordrar.map(x => new TplArbetsorderViewModel(
            x,
            trpftgId => allTransportforetag.find(x1 => x1.id === trpftgId),
            befraktareId => this.tplService.resolveBefraktareAndGetName(befraktareId),
            ao => this.tplService.aoHasBefraktareAndAreDifferent(ao)));
        this.sortArbetsorders();
    }

    private setNrOfSelectedAos() {
        this.nrOfSelectedAos = this.arbetsorders?.filter(x => x.isSelected).length ?? 0;
        this.canTilldela = this.nrOfSelectedAos > 0;
        this.allSelected = this.nrOfSelectedAos > 0 && this.nrOfSelectedAos === (this.arbetsorders?.length ?? 0);
    }

    private sortArbetsorders() {
        this.arbetsorders = _.orderBy(this.arbetsorders, [
            "isSelected",
            "northing"
        ], [
            "desc",
            "desc"
        ]);
    }

    private triggerDigestCycle(): void {
        this.changeDetectorRef.detectChanges();
    }
}
